import { useState, useEffect } from "react";
import { useAuth } from "../lib/AuthContext";
import { apiPost, apiGet } from "../lib/api";
import { COLORS } from "../lib/colors";

// Dark theme colors (match dashboard)
const C = {
  bgPrimary: "#0d1117", bgSecondary: "#161b22", bgTertiary: "#21262d",
  border: "#30363d", borderLight: "#21262d",
  textPrimary: "#e6edf3", textSecondary: "#8b949e", textMuted: "#6e7681",
  green: "#238636", green3: "#2ea043", yellow: "#d29922", red: "#da3633",
  blue: "#58a6ff", purple: "#a371f7", accent: "#2563eb",
};

function IndicatorSm({ score }) {
  const ind = score >= 70 ? { bg: C.green, label: "5" }
    : score >= 60 ? { bg: C.green3, label: "3" }
    : score >= 45 ? { bg: C.yellow, label: "!" }
    : { bg: C.red, label: "✕" };
  return (
    <div style={{
      width: 24, height: 24, borderRadius: "50%", display: "flex",
      alignItems: "center", justifyContent: "center", fontSize: "0.65rem",
      fontWeight: 700, background: ind.bg, color: "#fff", flexShrink: 0,
      fontFamily: "'IBM Plex Mono', monospace",
    }}>{ind.label}</div>
  );
}

function SourceBadge() {
  return (
    <span style={{
      fontSize: "0.6rem", padding: "2px 6px", borderRadius: 3, fontWeight: 700,
      background: C.purple + "30", color: C.purple, marginLeft: 8,
      textTransform: "uppercase", letterSpacing: 0.5,
    }}>docs</span>
  );
}

function CriterionRow({ code, name, value, indicator, rightLabel, subMetrics }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <div
        onClick={() => subMetrics?.length ? setExpanded(!expanded) : null}
        style={{
          padding: "0.75rem 1.25rem", display: "flex", alignItems: "center",
          borderBottom: `1px solid ${C.border}`,
          cursor: subMetrics?.length ? "pointer" : "default",
        }}
      >
        <IndicatorSm score={indicator} />
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace", fontSize: "0.8rem",
          color: C.textMuted, width: 50, flexShrink: 0, marginLeft: "0.5rem",
        }}>{code}</span>
        <span style={{ fontSize: "0.9rem", fontWeight: 600, color: C.textPrimary }}>{name}</span>
        <SourceBadge />
        <span style={{ flex: 1 }} />
        {value && <span style={{
          fontFamily: "'IBM Plex Mono', monospace", fontSize: "0.9rem",
          color: C.green, fontWeight: 700, marginRight: "0.5rem",
        }}>{value}</span>}
        {rightLabel && <span style={{ fontSize: "0.8rem", color: C.textMuted }}>{rightLabel}</span>}
        {subMetrics?.length > 0 && <span style={{ color: C.textMuted, fontSize: "0.7rem", marginLeft: 8 }}>{expanded ? "▲" : "▼"}</span>}
      </div>
      {expanded && subMetrics && (
        <div style={{ background: C.bgPrimary, borderBottom: `1px solid ${C.border}` }}>
          {subMetrics.map((sm, i) => (
            <div key={i} style={{
              padding: "0.5rem 1.25rem 0.5rem 5.5rem", display: "flex", alignItems: "center",
              borderBottom: `1px solid ${C.border}`, fontSize: "0.85rem",
            }}>
              <IndicatorSm score={sm.score} />
              <span style={{ width: 200, color: C.textSecondary, marginLeft: "0.75rem", flexShrink: 0 }}>{sm.name}</span>
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace", fontSize: "0.9rem",
                color: C.green, fontWeight: 700, width: 100,
              }}>{sm.value}</span>
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: "0.8rem", color: C.textMuted, textAlign: "right" }}>{sm.target}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default function ProgrammeAnalysis({ institutionId, programmeId }) {
  const { session } = useAuth();
  const token = session?.access_token;
  const [result, setResult] = useState(null);
  const [urls, setUrls] = useState([""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showScraper, setShowScraper] = useState(false);

  // Load existing gap report on mount
  useEffect(() => {
    if (!programmeId || !token) return;
    (async () => {
      try {
        const data = await apiGet(`/programme/${programmeId}/gap-report`, token);
        if (data && (data.readiness_score || data.ilo_count)) setResult(data);
      } catch (e) {}
    })();
  }, [programmeId, token]);

  // Fetch programme URL
  useEffect(() => {
    if (!programmeId || !institutionId || !token) return;
    (async () => {
      try {
        const progs = await apiGet(`/programmes/${institutionId}`, token);
        const prog = (progs.programmes || progs || []).find(p => p.id === programmeId);
        if (prog?.primary_url) setUrls([prog.primary_url]);
      } catch (e) {}
    })();
  }, [programmeId, institutionId, token]);

  const analyze = async () => {
    if (!programmeId) return;
    setLoading(true); setError("");
    try {
      const validUrls = urls.filter(u => u.trim());
      if (validUrls.length > 0) {
        try { await apiPost(`/programme/${programmeId}/scrape`, { urls: validUrls }, token); } catch (e) {}
      }
      const data = await apiGet(`/programme/${programmeId}/gap-report`, token);
      setResult(data);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  // Compute display data from gap report
  const iloCount = result?.ilo_count || 0;
  const pillarCov = result?.pillar_coverage || {};
  const iloAnalysis = result?.ilo_analysis || {};
  const hasK = iloAnalysis.has_knowledge;
  const hasS = iloAnalysis.has_skills;
  const hasA = iloAnalysis.has_attitudes;
  const weakVerbs = iloAnalysis.weak_verb_count || 0;
  // Use exact counts from API if available, otherwise estimate
  const kCount = iloAnalysis.knowledge_count ?? (hasK ? Math.max(1, Math.round(iloCount / 3)) : 0);
  const sCount = iloAnalysis.skills_count ?? (hasS ? Math.max(1, Math.round(iloCount / 3)) : 0);
  const aCount = iloAnalysis.attitudes_count ?? (hasA ? Math.max(1, iloCount - kCount - sCount) : 0);

  const pillarLabel = (v) => v >= 0.8 ? "Strong" : v >= 0.6 ? "Good" : v >= 0.4 ? "Borderline" : "Gap";
  const pillarScore = (v) => v >= 0.8 ? 80 : v >= 0.6 ? 65 : v >= 0.4 ? 50 : 30;
  const pillarIlos = (v) => v >= 0.8 ? "3 ILOs" : v >= 0.6 ? "2 ILOs" : v >= 0.4 ? "1 ILO" : "0 ILOs";

  const readinessScore = result?.readiness_score || 0;
  const programmeScore = iloCount >= 5 ? 84 : iloCount >= 3 ? 60 : 30;

  return (
    <div style={{ fontFamily: "'IBM Plex Sans', -apple-system, sans-serif", color: C.textPrimary }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Programme Analysis</h2>
      <p style={{ fontSize: 14, color: C.textMuted, marginBottom: 24 }}>
        Programme data, ILOs, accreditation pillar coverage, and curriculum structure.
      </p>

      {/* Programme header */}
      <div style={{
        background: C.bgSecondary, border: `1px solid ${C.border}`,
        borderRadius: 12, marginBottom: 16, overflow: "hidden",
      }}>
        <div style={{ padding: "1.25rem", display: "flex", alignItems: "center" }}>
          <div style={{
            width: 48, height: 48, borderRadius: "50%", display: "flex",
            alignItems: "center", justifyContent: "center", fontSize: "1.1rem",
            fontWeight: 700, background: programmeScore >= 70 ? C.green : programmeScore >= 45 ? C.yellow : C.red,
            color: "#fff", marginRight: "1rem",
          }}>5</div>
          <div>
            <div style={{ fontSize: "0.75rem", color: C.textMuted }}>Programme data analyzed</div>
            <div style={{ fontSize: "1.25rem", fontWeight: 700 }}>
              Programme <span style={{ fontWeight: 400, color: C.textSecondary, marginLeft: 8 }}>{programmeScore}/100</span>
            </div>
            <div style={{ fontSize: "0.8rem", color: C.textMuted }}>Chapter 2 • ILOs, pillars, curriculum</div>
          </div>
        </div>

        {result ? (
          <>
            {/* ILOs */}
            <CriterionRow code="2.2.2" name="Intended Learning Outcomes" indicator={iloCount >= 5 ? 80 : 35}
              value={`${iloCount} ILOs`} rightLabel={hasK && hasS && hasA ? "K/S/B balanced" : "incomplete"}
              subMetrics={[
                { score: kCount >= 2 ? 80 : 35, name: "Knowledge ILOs", value: String(kCount), target: "target: 2+" },
                { score: sCount >= 2 ? 80 : 35, name: "Skills ILOs", value: String(sCount), target: "target: 2+" },
                { score: aCount >= 2 ? 80 : 35, name: "Behaviour ILOs", value: String(aCount), target: "target: 2+" },
                { score: weakVerbs === 0 ? 80 : 50, name: "Measurable", value: weakVerbs === 0 ? "Yes" : `${weakVerbs} weak`, target: "action verbs" },
              ]} />

            {/* Pillars */}
            <CriterionRow code="Pillars" name="EFMD Pillar Coverage" indicator={Object.values(pillarCov).length >= 4 ? 80 : 50}
              value={`${Object.values(pillarCov).filter(v => v >= 0.4).length}/4`} rightLabel="all covered"
              subMetrics={[
                { score: pillarScore(pillarCov.International || 0), name: "International", value: pillarLabel(pillarCov.International || 0), target: pillarIlos(pillarCov.International || 0) },
                { score: pillarScore(pillarCov.Practice || 0), name: "Practice", value: pillarLabel(pillarCov.Practice || 0), target: pillarIlos(pillarCov.Practice || 0) },
                { score: pillarScore(pillarCov.ERS || 0), name: "ERS", value: pillarLabel(pillarCov.ERS || 0), target: pillarIlos(pillarCov.ERS || 0) },
                { score: pillarScore(pillarCov.Digital || 0), name: "Digital", value: pillarLabel(pillarCov.Digital || 0), target: pillarIlos(pillarCov.Digital || 0) },
              ]} />

            {/* Rationale */}
            <CriterionRow code="2.2.1" name="Programme Rationale" indicator={iloCount >= 5 ? 80 : 50}
              rightLabel={iloCount >= 5 ? "✔ clear objectives" : "incomplete"} subMetrics={[]} />

            {/* Structure */}
            <CriterionRow code="2.2.3" name="Structure & Coverage" indicator={80}
              rightLabel="✔ 120 ECTS" subMetrics={[]} />
          </>
        ) : (
          <div style={{ padding: "2rem", textAlign: "center", color: C.textMuted, fontSize: 14 }}>
            No programme data yet. Use the scraper below or upload documents to populate.
          </div>
        )}
      </div>

      {/* Critical gaps */}
      {result?.critical_gaps?.length > 0 && (
        <div style={{
          background: C.red + "15", border: `1px solid ${C.red}30`, borderRadius: 12,
          padding: "1rem 1.25rem", marginBottom: 16,
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.red, marginBottom: 8 }}>Critical Gaps</div>
          {result.critical_gaps.map((gap, i) => (
            <div key={i} style={{ fontSize: 13, color: C.red, opacity: 0.85, marginBottom: 4 }}>– {gap}</div>
          ))}
        </div>
      )}

      {/* Recommendations */}
      {result?.recommendations?.length > 0 && (
        <div style={{
          background: C.bgSecondary, border: `1px solid ${C.border}`, borderRadius: 12,
          padding: "1rem 1.25rem", marginBottom: 16,
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary, marginBottom: 8 }}>Recommendations</div>
          {result.recommendations.map((rec, i) => (
            <div key={i} style={{ fontSize: 13, color: C.textSecondary, marginBottom: 4 }}>– {rec}</div>
          ))}
        </div>
      )}

      {/* Scraper (collapsible) */}
      <div style={{
        background: C.bgSecondary, border: `1px solid ${C.border}`, borderRadius: 12,
        overflow: "hidden",
      }}>
        <div
          onClick={() => setShowScraper(!showScraper)}
          style={{
            padding: "0.75rem 1.25rem", display: "flex", alignItems: "center",
            cursor: "pointer", fontSize: 14, fontWeight: 600, color: C.textSecondary,
          }}
        >
          <span style={{ flex: 1 }}>🔍 Re-analyze Programme Website</span>
          <span style={{ fontSize: "0.7rem" }}>{showScraper ? "▲" : "▼"}</span>
        </div>
        {showScraper && (
          <div style={{ padding: "0 1.25rem 1.25rem" }}>
            {urls.map((url, i) => (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <input value={url} onChange={(e) => { const u = [...urls]; u[i] = e.target.value; setUrls(u); }}
                  placeholder="https://www.university.edu/programme"
                  style={{
                    flex: 1, padding: "8px 12px", fontSize: 14, background: C.bgTertiary,
                    border: `1px solid ${C.border}`, borderRadius: 8, color: C.textPrimary, outline: "none",
                  }} />
                {urls.length > 1 && (
                  <button onClick={() => setUrls(urls.filter((_, idx) => idx !== i))} style={{
                    padding: "8px 12px", fontSize: 12, background: C.red + "20", color: C.red,
                    border: "none", borderRadius: 6, cursor: "pointer",
                  }}>✕</button>
                )}
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button onClick={() => setUrls([...urls, ""])} style={{
                padding: "6px 14px", fontSize: 12, background: C.bgTertiary,
                color: C.textSecondary, border: `1px solid ${C.border}`, borderRadius: 6, cursor: "pointer",
              }}>+ Add URL</button>
              <button onClick={analyze} disabled={loading} style={{
                padding: "8px 20px", fontSize: 13, fontWeight: 600,
                background: loading ? C.bgTertiary : C.accent, color: loading ? C.textMuted : "#fff",
                border: "none", borderRadius: 8, cursor: loading ? "not-allowed" : "pointer",
              }}>{loading ? "Analyzing..." : "Analyze Programme"}</button>
            </div>
            {error && <div style={{ color: C.red, fontSize: 13, marginTop: 8 }}>{error}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
